import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { ExchangeRate } from "../../../types/admin";

interface ExchangeRateTableProps {
  rates: ExchangeRate[];
  loading?: boolean;
  onEdit: (rate: ExchangeRate) => void;
  onDelete: (rate: ExchangeRate) => void;
}

const formatRate = (value: number) => value.toFixed(4);

const ExchangeRateTable: React.FC<ExchangeRateTableProps> = ({
  rates,
  loading,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.exchangeRates.sellValue")}</TableCell>
              <TableCell>{t("admin.exchangeRates.buyValue")}</TableCell>
              <TableCell>{t("admin.exchangeRates.isActive")}</TableCell>
              <TableCell>{t("admin.exchangeRates.comment")}</TableCell>
              <TableCell>{t("admin.exchangeRates.created")}</TableCell>
              <TableCell>{t("admin.exchangeRates.updated")}</TableCell>
              <TableCell align="right">{t("admin.exchangeRates.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={28} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : rates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.exchangeRates.noRatesFound")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rates.map((rate) => (
                <TableRow key={rate.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatRate(rate.sellValue)} c.
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatRate(rate.buyValue)} USD
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rate.isActive ? t("admin.exchangeRates.active") : t("admin.exchangeRates.inactive")}
                      color={rate.isActive ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {rate.comment ? (
                      <Tooltip title={rate.comment}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {rate.comment}
                        </Typography>
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(rate.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {rate.updatedAt
                      ? new Date(rate.updatedAt).toLocaleString()
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => onEdit(rate)}
                      aria-label="edit exchange rate"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => onDelete(rate)}
                      aria-label="delete exchange rate"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ExchangeRateTable;

